import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaTimes, FaTag, FaLeaf, FaPlus, FaMinus } from 'react-icons/fa';
import { useCart } from '../../context/CartContext';
import {
  formatCartLineCustomiseLabel,
  formatCartLineSummaryParts,
} from '../../pages/OurMenu/cartAddOnUtils';
import {
  getCartLinesForMenuItem,
  getEnabledVariants,
  getMenuItemUnavailableLabel,
  getVariantUnavailableLabel,
  isMenuItemOrderable,
  isVariableMenuItem,
  isVariantOrderable,
  menuItemHasAddOns,
} from '../../pages/OurMenu/ourMenuUtils';
import MenuItemActions from '../../pages/OurMenu/MenuItemActions';
import './MenuItemModal.css';

const parentFallbackDescription = (item) =>
  `A delicious, freshly prepared ${String(item.category || 'menu')
    .toLowerCase()} dish made with hand-picked ingredients and traditional recipes. No preservatives, no artificial flavours — just pure home-style goodness delivered to your door.`;

/**
 * @param {{
 *   item: object,
 *   onClose: () => void,
 *   openIntent?: 'cart' | 'plan' | null,
 *   editCartKey?: string | null,
 *   editPlanLineKey?: string | null,
 *   editSlotId?: string | null,
 *   initialVariantId?: number | string | null,
 *   initialAddOnIds?: number[],
 *   initialAddOnQtys?: Record<number, number> | null,
 *   initialQuantity?: number,
 * }} props
 */
const MenuItemModal = ({
  item,
  onClose,
  openIntent = null,
  editCartKey = null,
  editPlanLineKey = null,
  editSlotId = null,
  initialVariantId = null,
  initialAddOnIds = null,
  initialAddOnQtys = null,
  initialQuantity = 1,
}) => {
  const { cartItems } = useCart();
  const variable = isVariableMenuItem(item);
  const isEditMode = Boolean(editCartKey || editPlanLineKey);
  const isPlanEdit = Boolean(editPlanLineKey);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  /** @type {[Record<string, number>, Function]} */
  const [addOnQtys, setAddOnQtys] = useState({});
  const [plateQty, setPlateQty] = useState(1);
  const [needsSizePrompt, setNeedsSizePrompt] = useState(false);
  const [sizePulse, setSizePulse] = useState(false);
  const variantsSectionRef = useRef(null);
  const skipAddOnResetRef = useRef(false);
  const primaryAction = isPlanEdit || openIntent === 'plan' ? 'plan' : 'cart';
  // Cart and plan both pick host qty + independent add-on qtys in the modal.
  const showPlateQty = primaryAction === 'cart' || primaryAction === 'plan' || isEditMode;

  const seedAddOnQtys = () => {
    /** @type {Record<string, number>} */
    const next = {};
    if (initialAddOnQtys && typeof initialAddOnQtys === 'object') {
      for (const [key, value] of Object.entries(initialAddOnQtys)) {
        const id = Number(key);
        const qty = Math.max(0, Math.round(Number(value) || 0));
        if (Number.isFinite(id) && id > 0 && qty > 0) next[String(id)] = qty;
      }
    } else if (Array.isArray(initialAddOnIds)) {
      for (const raw of initialAddOnIds) {
        const id = Number(raw);
        if (Number.isFinite(id) && id > 0) next[String(id)] = 1;
      }
    }
    return next;
  };

  // Hydrate (or reset) when opening a different item / edit session.
  useEffect(() => {
    const qtySeed = Math.max(1, Math.round(Number(initialQuantity) || 1));
    const seeded = seedAddOnQtys();
    const hasSeed =
      isEditMode ||
      Object.keys(seeded).length > 0 ||
      initialVariantId != null;

    if (hasSeed) {
      skipAddOnResetRef.current = true;
      setSelectedVariantId(
        initialVariantId != null && initialVariantId !== ''
          ? initialVariantId
          : null,
      );
      setAddOnQtys(seeded);
      setPlateQty(qtySeed);
    } else {
      setSelectedVariantId(null);
      setAddOnQtys({});
      setPlateQty(1);
    }
    setNeedsSizePrompt(false);
    setSizePulse(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed only on open identity
  }, [item?.id, editCartKey, editPlanLineKey]);

  useEffect(() => {
    if (skipAddOnResetRef.current) {
      skipAddOnResetRef.current = false;
      return;
    }
    setAddOnQtys({});
  }, [selectedVariantId]);

  useEffect(() => {
    if (selectedVariantId != null) {
      setNeedsSizePrompt(false);
      setSizePulse(false);
    }
  }, [selectedVariantId]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const selectedVariant = useMemo(() => {
    if (!variable || selectedVariantId == null) return null;
    return (item.variants || []).find(
      (v) => String(v.itemID) === String(selectedVariantId),
    );
  }, [variable, item?.variants, selectedVariantId]);

  const orderable = variable
    ? getEnabledVariants(item).length > 0
    : isMenuItemOrderable(item);
  const unavailableLabel = getMenuItemUnavailableLabel(item);

  const promptSelectSize = () => {
    setNeedsSizePrompt(true);
    setSizePulse(true);
    variantsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => setSizePulse(false), 900);
  };

  const displayImage = selectedVariant?.image || item.image;
  const displayFallback =
    selectedVariant?.fallbackImage || item.fallbackImage || item.image;
  const displayDescription =
    (selectedVariant?.description && selectedVariant.description.trim()) ||
    (item.description && item.description.trim()) ||
    parentFallbackDescription(item);
  const displaySubtitle =
    selectedVariant?.name ||
    (selectedVariant?.label ? `${item.name} · ${selectedVariant.label}` : null);

  const hostReady = !variable || selectedVariant != null;
  const availableAddOns = useMemo(() => {
    if (!hostReady) return [];
    const list = selectedVariant?.addOns ?? item?.addOns;
    return Array.isArray(list) ? list : [];
  }, [hostReady, selectedVariant, item?.addOns]);

  const selectedAddOns = useMemo(
    () =>
      availableAddOns
        .map((a) => {
          const id = Number(a.addOnItemID);
          const qty = Math.max(0, Math.round(Number(addOnQtys[String(id)]) || 0));
          if (!qty) return null;
          return { ...a, quantity: qty };
        })
        .filter(Boolean),
    [availableAddOns, addOnQtys],
  );

  const setAddOnQty = (addOnItemID, nextQty) => {
    const id = Number(addOnItemID);
    if (!Number.isFinite(id) || id <= 0) return;
    const qty = Math.max(0, Math.min(99, Math.round(Number(nextQty) || 0)));
    setAddOnQtys((prev) => {
      const next = { ...prev };
      if (qty <= 0) delete next[String(id)];
      else next[String(id)] = qty;
      return next;
    });
  };

  const hostUnitPrice = selectedVariant
    ? Number(selectedVariant.price ?? 0)
    : variable
      ? null
      : Number(item.price ?? 0);

  const addOnMoney = selectedAddOns.reduce(
    (sum, a) => sum + Number(a.price || 0) * Math.max(1, Number(a.quantity) || 1),
    0,
  );
  const lineTotal =
    hostUnitPrice != null && Number.isFinite(hostUnitPrice)
      ? hostUnitPrice * plateQty + addOnMoney
      : null;

  const priceDisplay =
    lineTotal != null && Number.isFinite(lineTotal)
      ? `₹${lineTotal.toFixed(2)}`
      : selectedVariant?.priceDisplay
        ? selectedVariant.priceDisplay
        : variable
          ? item.fromPriceDisplay || item.priceDisplay || 'Select an option'
          : item.priceDisplay;

  const setQty = (next) => {
    setPlateQty(Math.max(1, Math.min(99, Math.round(Number(next) || 1))));
  };

  const familyLines = useMemo(
    () => (item ? getCartLinesForMenuItem(cartItems, item) : []),
    [cartItems, item],
  );
  const showAlreadyStrip =
    !isEditMode &&
    primaryAction === 'cart' &&
    familyLines.length > 0 &&
    (menuItemHasAddOns(item) ||
      familyLines.some((l) => Array.isArray(l.addOns) && l.addOns.length > 0));

  const youGetHostLabel = selectedVariant
    ? `${item.name} - ${selectedVariant.label || selectedVariant.name || 'Option'}`
    : item.name;

  if (!item) return null;

  return (
    <div className="mim-overlay" onClick={onClose}>
      <div className="mim-modal" onClick={(e) => e.stopPropagation()}>
        <button className="mim-close" onClick={onClose} aria-label="Close">
          <FaTimes />
        </button>

        <div className="mim-img-wrap">
          <img
            key={displayImage}
            src={displayImage}
            alt={displaySubtitle || item.name}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = displayFallback;
            }}
          />
          <span className="mim-category">{item.category}</span>
        </div>

        <div className="mim-body">
          <h2 className="mim-name">{item.name}</h2>
          {isEditMode ? (
            <p className="mim-edit-banner" role="status">
              {isPlanEdit
                ? 'Editing plan item — change option, add-ons, or quantity, then update.'
                : 'Editing cart item — change option, add-ons, or quantity, then update.'}
            </p>
          ) : null}

          {showAlreadyStrip ? (
            <div className="mim-already" role="status">
              <p className="mim-already__label">Already in cart</p>
              <ul className="mim-already__list">
                {familyLines.map((line) => {
                  const parts = formatCartLineSummaryParts(line);
                  return (
                    <li key={line.cartKey || `${line.id}-${line.addedAt}`}>
                      <span className="mim-already__title">
                        {formatCartLineCustomiseLabel(line) || parts.hostLine}
                      </span>
                      <span className="mim-already__detail">{parts.hostLine}</span>
                      {parts.addonLines.length ? (
                        <span className="mim-already__detail">
                          {parts.addonLines.join(' · ')}
                        </span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}

          {displaySubtitle && selectedVariant ? (
            <p className="mim-variant-name">{displaySubtitle}</p>
          ) : null}

          <div className="mim-meta">
            <span className="mim-meta__item">
              <FaTag className="mim-meta__icon" />
              {item.category}
            </span>
            <span className="mim-meta__item">
              <FaLeaf className="mim-meta__icon mim-meta__icon--green" />
              Fresh Daily
            </span>
          </div>

          <p className="mim-desc">{displayDescription}</p>

          {variable ? (
            <div
              ref={variantsSectionRef}
              className={`mim-variants${needsSizePrompt ? ' mim-variants--needs-selection' : ''}${
                sizePulse ? ' mim-variants--pulse' : ''
              }`}
            >
              <p className="mim-variants__label">Choose size</p>
              <div
                className="mim-variants__list"
                role="radiogroup"
                aria-label={`Options for ${item.name}`}
                aria-invalid={needsSizePrompt || undefined}
              >
                {(item.variants || []).map((v) => {
                  const enabled = isVariantOrderable(v);
                  const selected = String(selectedVariantId) === String(v.itemID);
                  return (
                    <button
                      key={v.itemID}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      disabled={!enabled}
                      className={`mim-variant${selected ? ' mim-variant--selected' : ''}${
                        !enabled ? ' mim-variant--disabled' : ''
                      }`}
                      onClick={() => {
                        if (!enabled) return;
                        setSelectedVariantId(v.itemID);
                      }}
                    >
                      <span className="mim-variant__label">{v.label}</span>
                      <span className="mim-variant__price">
                        {enabled
                          ? v.priceDisplay
                          : getVariantUnavailableLabel(v)}
                      </span>
                    </button>
                  );
                })}
              </div>
              {needsSizePrompt || !selectedVariant ? (
                <p
                  className={`mim-variants__hint${needsSizePrompt ? ' mim-variants__hint--error' : ''}`}
                  role="status"
                  aria-live="polite"
                >
                  {needsSizePrompt ? 'Please choose an option' : 'Select an option to add this item.'}
                </p>
              ) : null}
            </div>
          ) : (
            <ul className="mim-highlights">
              <li>✔ No preservatives</li>
              <li>✔ Home-style preparation</li>
              <li>✔ Delivered fresh &amp; hot</li>
            </ul>
          )}

          {hostReady && availableAddOns.length > 0 ? (
            <div className="mim-addons">
              <p className="mim-addons__label">Add-ons – separate qty</p>
              <p className="mim-addons__hint">
                These stay at this qty if you later raise the item.
              </p>
              <ul className="mim-addons__list">
                {availableAddOns.map((addon) => {
                  const id = Number(addon.addOnItemID);
                  const qty = Math.max(0, Math.round(Number(addOnQtys[String(id)]) || 0));
                  const active = qty > 0;
                  return (
                    <li key={addon.addOnItemID}>
                      <div
                        className={`mim-addon mim-addon--stepper${active ? ' mim-addon--checked' : ''}`}
                      >
                        <div className="mim-addon__copy">
                          <span className="mim-addon__name">{addon.name}</span>
                          <span className="mim-addon__price">
                            +₹{Number(addon.price || 0).toFixed(0)} each
                          </span>
                        </div>
                        {active ? (
                          <div
                            className="mim-qty__stepper mim-addon__stepper"
                            role="group"
                            aria-label={`Quantity of ${addon.name}`}
                          >
                            <button
                              type="button"
                              className="mim-qty__btn"
                              onClick={() => setAddOnQty(id, qty - 1)}
                              aria-label={`Decrease ${addon.name}`}
                            >
                              <FaMinus />
                            </button>
                            <span className="mim-qty__value" aria-live="polite">
                              {qty}
                            </span>
                            <button
                              type="button"
                              className="mim-qty__btn"
                              onClick={() => setAddOnQty(id, qty + 1)}
                              aria-label={`Increase ${addon.name}`}
                            >
                              <FaPlus />
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="mim-addon__add"
                            onClick={() => setAddOnQty(id, 1)}
                          >
                            Add
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : null}

          {hostReady && showPlateQty ? (
            <div className="mim-youll-get" aria-live="polite">
              <p className="mim-youll-get__label">You&apos;ll get</p>
              <ul className="mim-youll-get__list">
                <li className="mim-youll-get__host">
                  {plateQty}x {youGetHostLabel}
                </li>
                {selectedAddOns.map((a) => (
                  <li key={a.addOnItemID} className="mim-youll-get__addon">
                    {Math.max(1, Number(a.quantity) || 1)}x {a.name}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {showPlateQty ? (
            <div className="mim-qty">
              <p className="mim-qty__label">Quantity</p>
              <div
                className="mim-qty__stepper"
                role="group"
                aria-label={`Quantity of ${item.name}`}
              >
                <button
                  type="button"
                  className="mim-qty__btn"
                  onClick={() => setQty(plateQty - 1)}
                  disabled={plateQty <= 1}
                  aria-label="Decrease quantity"
                >
                  <FaMinus />
                </button>
                <span className="mim-qty__value" aria-live="polite">
                  {plateQty}
                </span>
                <button
                  type="button"
                  className="mim-qty__btn"
                  onClick={() => setQty(plateQty + 1)}
                  aria-label="Increase quantity"
                >
                  <FaPlus />
                </button>
              </div>
              {hostUnitPrice != null && Number.isFinite(hostUnitPrice) ? (
                <p className="mim-qty__unit">
                  ₹{hostUnitPrice.toFixed(2)} each
                  {addOnMoney > 0 ? ` · +₹${addOnMoney.toFixed(2)} add-ons` : ''}
                </p>
              ) : null}
            </div>
          ) : null}

        </div>

        <div className="mim-footer">
          <div className="mim-footer__price">
            <span className="mim-price">{priceDisplay}</span>
            {selectedAddOns.length > 0 ? (
              <span className="mim-price-note">
                {selectedAddOns
                  .map(
                    (a) =>
                      `${a.name} (+₹${(Number(a.price) * Math.max(1, Number(a.quantity) || 1)).toFixed(0)})`,
                  )
                  .join(' · ')}
              </span>
            ) : plateQty > 1 && hostUnitPrice != null ? (
              <span className="mim-price-note">{plateQty} plates</span>
            ) : null}
          </div>
          <MenuItemActions
            item={item}
            orderable={orderable}
            unavailableLabel={unavailableLabel}
            variant="modal"
            className="mim-btn"
            addedClassName="mim-btn--added"
            planClassName="mim-btn mim-btn--plan"
            selectedVariant={selectedVariant}
            selectedAddOns={selectedAddOns}
            primaryAction={primaryAction}
            editCartKey={editCartKey}
            editPlanLineKey={editPlanLineKey}
            editSlotId={editSlotId}
            draftQuantity={plateQty}
            onAfterAdd={onClose}
            onOpenOptions={variable && !selectedVariant ? promptSelectSize : undefined}
          />
        </div>
      </div>
    </div>
  );
};

export default MenuItemModal;
